import AsyncStorage from '@react-native-async-storage/async-storage';
import { createClient, Session } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import { Report, Severity, sortReportsByAgentOrder } from './data';
import { loadInventorySnapshots, updateReportAction } from './productApi';
import { configuredAppUrl, supabasePublishableKey, supabaseUrl } from './runtime';

export const supabaseConfigured = Boolean(supabaseUrl && supabasePublishableKey);

export const supabase = createClient(
  supabaseUrl || 'https://invalid.supabase.co',
  supabasePublishableKey || 'missing-publishable-key',
  {
    auth: {
      ...(Platform.OS === 'web' ? {} : { storage: AsyncStorage }),
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: Platform.OS === 'web',
    },
  },
);

type PostRow = {
  id: string;
  agent_id: string;
  created_at: string;
  headline: string;
  body: string;
  kpis: Report['kpis'] | null;
  hero: Report['hero'] | null;
  series: number[] | null;
  severity: Severity | null;
  attention: string | null;
  action: string | null;
  source: string | null;
  source_at: string | null;
  source_note: string | null;
  confidence: Report['confidence'] | null;
  assigned_to: string | null;
  resolved_at: string | null;
  flags: string[] | null;
  acknowledged_by: string | null;
};

const laneMap: Record<string, string> = {
  owner: 'owner',
  'shopify-retail': 'retail',
  'shopify-wholesale': 'wholesale',
  'fishbowl-commerce': 'fishbowl',
  markettime: 'markettime',
  quickbooks: 'quickbooks',
};

export const backendAgentId: Record<string, string> = Object.fromEntries(
  Object.entries(laneMap).map(([backend, app]) => [app, backend]),
);

function plainSummary(body = '') {
  return body
    .replace(/^###\s+/gm, '')
    .replace(/^[-*]\s+/gm, '')
    .replace(/\*\*/g, '')
    .replace(/\n{2,}/g, ' ')
    .trim();
}

function money(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

export function postToReport(row: PostRow): Report {
  const hero = row.hero ?? { value: 'Unavailable', label: 'report value', sub: '' };
  const payment = hero.paymentPending;
  const paymentSummary = payment
    ? `Payment pending: ${payment.totalCount.toLocaleString()} wholesale orders totaling ${money(payment.totalValue)}; ${payment.todayCount.toLocaleString()} totaling ${money(payment.todayTotal)} are from today, and ${payment.olderCount.toLocaleString()} totaling ${money(payment.olderTotal)} are earlier orders. `
    : '';
  return {
    id: row.id,
    agentId: laneMap[row.agent_id] ?? row.agent_id,
    time: new Date(row.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
    headline: row.headline,
    severity: row.severity ?? 'fyi',
    attention: row.attention ?? '',
    action: row.action ?? 'Review this report with the lane owner.',
    hero,
    kpis: Array.isArray(row.kpis) ? row.kpis.slice(0, 3) : [],
    series: Array.isArray(row.series) ? row.series.slice(-7) : undefined,
    source: row.source ?? 'Blonde report',
    sourceAt: row.source_at ?? undefined,
    sourceNote: row.source_note ?? undefined,
    confidence: row.confidence ?? 'low',
    summary: paymentSummary + (plainSummary(row.body) || row.headline),
  };
}

export async function sendEmailCode(email: string) {
  return supabase.auth.signInWithOtp({
    email: email.trim().toLowerCase(),
    options: configuredAppUrl ? { emailRedirectTo: configuredAppUrl } : undefined,
  });
}

export async function loadProductionData(session: Session) {
  const [postsResult, peopleResult, inventoryRequest] = await Promise.allSettled([
    supabase
      .from('posts')
      .select('id,agent_id,created_at,headline,body,kpis,hero,series,severity,attention,action,source,source_at,source_note,confidence,assigned_to,resolved_at,flags,acknowledged_by')
      .order('created_at', { ascending: false })
      .limit(250),
    supabase.from('people').select('id,name,role,color,email,avatar_url,allowed_agents').order('created_at', { ascending: true }),
    loadInventorySnapshots(session),
  ]);
  if (postsResult.status === 'rejected') throw postsResult.reason;
  if (peopleResult.status === 'rejected') throw peopleResult.reason;
  const { data: posts, error: postsError } = postsResult.value;
  const { data: people, error: peopleError } = peopleResult.value;
  const inventoryResult = inventoryRequest.status === 'fulfilled' ? inventoryRequest.value : { snapshots: [] };
  if (postsError) throw postsError;
  if (peopleError) throw peopleError;
  const rows = (posts ?? []) as PostRow[];
  const latest = new Map<string, PostRow>();
  rows.forEach((row) => { if (!latest.has(row.agent_id)) latest.set(row.agent_id, row); });
  const inventories = new Map((inventoryResult.snapshots ?? []).map((snapshot) => [snapshot.laneId, snapshot]));
  const reports = sortReportsByAgentOrder([...latest.values()].map((row) => {
    const report = postToReport(row);
    const inventory = inventories.get(row.agent_id as 'shopify-retail' | 'shopify-wholesale');
    if (!inventory) return report;
    const inventorySummary = `Inventory: ${inventory.summary.itemCount.toLocaleString()} active merchandise variants, ${inventory.summary.totalShopifyOnHand.toLocaleString()} Shopify units on hand, ${inventory.summary.outOfStock.toLocaleString()} out of stock, ${inventory.summary.lowStock.toLocaleString()} low stock, and ${inventory.summary.fishbowlMatched.toLocaleString()} exact Fishbowl SKU matches. `;
    return { ...report, inventory, summary: inventorySummary + report.summary };
  }));
  const history = rows.map((row) => ({
    ...postToReport(row),
    date: new Date(row.created_at).toISOString().slice(0, 10),
  }));
  const state = Object.fromEntries(rows.map((row) => [row.id, {
    acknowledged: Array.isArray(row.flags) && row.flags.includes('acknowledged'),
    acknowledgedBy: row.acknowledged_by ?? undefined,
    assignedTo: row.assigned_to ?? undefined,
    resolved: Boolean(row.resolved_at),
  }]));
  return { reports, history, state, people: people ?? [] };
}

export async function setReportAcknowledged(session: Session, id: string, acknowledged: boolean) {
  await updateReportAction(session, id, 'acknowledge', acknowledged);
}

export async function setReportResolved(session: Session, id: string, resolved: boolean) {
  await updateReportAction(session, id, 'resolve', resolved);
}

export async function setReportAssignee(session: Session, id: string, assignedTo?: string) {
  await updateReportAction(session, id, 'assign', assignedTo ?? null);
}

export async function askAgent(session: Session, agentId: string, message: string, history: { role: string; content: string }[]) {
  let response: Response;
  try {
    response = await fetch(`${supabaseUrl}/functions/v1/blonde/chat`, {
      method: 'POST',
      headers: {
        apikey: supabasePublishableKey,
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ agent_id: agentId, message, history }),
    });
  } catch {
    throw new Error('The AI connection was interrupted. Please try again.');
  }
  const result = await response.json().catch(() => ({ error: 'The AI service returned an unreadable response. Please try again.' }));
  if (!response.ok) throw new Error(result.error || 'Agent unavailable');
  return String(result.reply ?? '');
}
